import { useContext, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useSearchParams } from "react-router-dom";
import UserContext from "../../../state/UserContext";
import { Sidebar } from "../../../components/common";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";
import { CreateAnalyticsPageFormData } from "../../../types/analytics/admin/CreateAnalyticsPageFormData";
import CreateAnalyticsPage from "./CreateAnalyticsPage";
import CreateAnalyticsSimpleWidget from "./CreateAnalyticsSimpleWidget";
import { useAnalyticsAdminApi } from "../../../hooks/useAnalyticsAdminApi";
import SmallLoader from "../../../components/loaders/SmallLoader";
import Toast from "../../../components/common/Toast";
import { ToastConfig } from "../../../components/common/ToastConfig";
import { CreateAnalyticsPageApiResponse } from "../../../types/analytics/admin/CreateAnalyticsPageApiResponse";
import permissionHelper from "../../../helpers/permissionHelper";

interface DynamicAnalyticsEditorMenuProps {
  /** When provided, this unlocks options like adding widgets to the page, which can only be performed on dynamic pages, not pages like Usage */
  pageId: number | undefined;
}

const DynamicAnalyticsEditorMenu = ({
  pageId,
}: DynamicAnalyticsEditorMenuProps) => {
  const auth = useAuth();
  const userContext = useContext(UserContext);
  const [searchParams] = useSearchParams();
  const hasEditParam = searchParams.has("edit");

  // Collapsible menu state
  const [collapsed, setCollapsed] = useState(false);
  // Sidebar state
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarType, setSidebarType] = useState<
    "ADD-SIMPLE-WIDGET" | "ADD-PAGE" | null
  >(null);
  // Toast state
  const [toastConfig, setToastConfig] = useState<ToastConfig>(
    ToastConfig.hidden()
  );

  // API hook for adding pages and widgets
  const {
    // New page states
    newPageLoading: isAddPageLoading,
    newPageError: addPageError,
    addNewPage,
    // New simple widget states
    newSimpleWidgetLoading: isAddSimpleWidgetLoading,
    newSimpleWidgetError: addSimpleWidgetError,
    addNewSimpleWidget,
  } = useAnalyticsAdminApi(auth.user?.access_token);

  if (
    !hasEditParam ||
    !permissionHelper.canAccessUrlOnlyAdminTools(userContext)
  ) {
    return null;
  }

  // Sidebar title logic
  let sidebarTitle = "";
  switch (sidebarType) {
    case "ADD-PAGE":
      sidebarTitle = "Add Page";
      break;
    case "ADD-SIMPLE-WIDGET":
      sidebarTitle = "Add Widget";
      break;
    default:
      sidebarTitle = "";
  }

  // Sidebar close logic as a function
  const handleSidebarClose = () => {
    setSidebarOpen(false);
    setSidebarType(null);
  };

  // Handling successful page addition
  const handleAddPageSuccess = (data: CreateAnalyticsPageApiResponse) => {
    if (data.success) {
      // Show the success toast
      setToastConfig(
        ToastConfig.success(
          "Page added!",
          "Your new analytics page was saved successfully."
        )
      );

      // Update the user context with the new page
      userContext.setUser({
        ...userContext.user,
        analyticsNavItems: data.newNavItemList,
      });

      // Close the sidebar
      handleSidebarClose();
    } else {
      setToastConfig(
        ToastConfig.error(
          "Error adding page",
          "There was an error saving your new analytics page: " +
            (data.errorMessage || "Unknown error")
        )
      );
    }
  };

  return (
    <div className="fixed top-20 right-6 z-40 w-72 bg-white border border-gray-200 rounded-lg shadow-lg">
      {/* Collapsible header */}
      <button
        className="w-full flex items-center justify-between px-4 py-3 bg-gray-100 rounded-t-lg hover:bg-gray-200 focus:outline-none"
        onClick={() => setCollapsed((c) => !c)}
        aria-expanded={!collapsed}
      >
        <span className="font-semibold text-gray-700">Analytics Editor</span>
        <span className="ml-2 text-gray-500">
          {collapsed ? (
            <FontAwesomeIcon icon={faChevronDown} size="sm" />
          ) : (
            <FontAwesomeIcon icon={faChevronUp} size="sm" />
          )}
        </span>
      </button>
      {/* Menu content */}
      {!collapsed && (
        <div className="p-4 flex flex-col gap-3">
          <button
            className="btn-primary w-full"
            onClick={() => {
              setSidebarType("ADD-PAGE");
              setSidebarOpen(true);
            }}
          >
            + Add New Page
          </button>
          {pageId && (
            <button
              className="btn-primary w-full"
              onClick={() => {
                setSidebarType("ADD-SIMPLE-WIDGET");
                setSidebarOpen(true);
              }}
            >
              + Add Simple Widget
            </button>
          )}
        </div>
      )}
      <Sidebar
        isOpen={sidebarOpen}
        onOpenChange={(open) => {
          setSidebarOpen(open);
          if (!open) setSidebarType(null);
        }}
        title={sidebarTitle}
        showCloseIcon
      >
        {sidebarType === "ADD-PAGE" && (
          <>
            <CreateAnalyticsPage
              onCancel={handleSidebarClose}
              onSubmit={(data: CreateAnalyticsPageFormData) => {
                addNewPage(data, handleAddPageSuccess);
              }}
              analyticsPages={userContext.user.analyticsNavItems}
              isSubmitting={isAddPageLoading}
            />
            {isAddPageLoading && (
              <div className="my-4">
                <SmallLoader />
                <div className="text-center text-gray-500 mt-2">
                  Saving new page...
                </div>
              </div>
            )}
            {addPageError && (
              <div className="text-center text-red-500 mt-2">
                Error: {addPageError?.message || addPageError.toString()}
              </div>
            )}
          </>
        )}
        {sidebarType === "ADD-SIMPLE-WIDGET" && (
          <>
            <CreateAnalyticsSimpleWidget
              fromPageId={pageId}
              onCancel={handleSidebarClose}
              isSubmitting={isAddSimpleWidgetLoading}
              onSubmit={(data) => {
                addNewSimpleWidget(data, (res) => {
                  if (res.success) {
                    setToastConfig(
                      ToastConfig.success(
                        "Widget added!",
                        "Your new widget was saved successfully. Refresh the page to see it."
                      )
                    );
                    handleSidebarClose();
                  } else {
                    setToastConfig(
                      ToastConfig.error(
                        "Error adding widget",
                        "There was an error saving your new widget: " +
                          (res.errorMessage || "Unknown error")
                      )
                    );
                  }
                });
              }}
            />
            {isAddSimpleWidgetLoading && (
              <div className="my-4">
                <SmallLoader />
                <div className="text-center text-gray-500 mt-2">
                  Saving new widget...
                </div>
              </div>
            )}
            {addSimpleWidgetError && (
              <div className="text-center text-red-500 mt-2">
                Error:{" "}
                {addSimpleWidgetError?.message ||
                  addSimpleWidgetError.toString()}
              </div>
            )}
          </>
        )}
      </Sidebar>

      {/* Toast for successful save */}
      {toastConfig.show && (
        <Toast
          title={toastConfig.title}
          message={toastConfig.message}
          type={toastConfig.type}
          onClose={() => setToastConfig(ToastConfig.hidden())}
        />
      )}
    </div>
  );
};

export default DynamicAnalyticsEditorMenu;
